import { Box, BoxProps, Grid, Stack, Typography, Button, Alert, SvgIconProps } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useErrorBoundary } from 'react-error-boundary'
import { mergeSx } from '@/lib/mergeSx'
import useIntl from '@/lib/hooks/useIntl'

const defaultOnGoBack = () => window.history.back()

export type PageErrorFallbackProps = {
  title?: string
  description?: string
  message?: string
  withUseErrorBoundary?: boolean
  showClearFilters?: boolean
  onRetry?: () => void
  onGoBack?: () => void
  slots?: {
    root?: React.ComponentType<BoxProps>
    icon?: React.ComponentType<SvgIconProps>
  }
  slotProps?: {
    root?: Partial<BoxProps>
    icon?: Partial<SvgIconProps>
  }
} & Omit<BoxProps, 'title'>

const handleClearFilters = () => {
  const url = new URL(window.location.href)
  url.search = ''
  url.searchParams.set('clear_filter', 'true')
  window.location.href = url.toString()
}

const PageErrorFallback = ({
  title,
  description,
  message,
  withUseErrorBoundary,
  showClearFilters,
  onRetry,
  onGoBack = defaultOnGoBack,
  slots,
  slotProps,
  sx,
  children,
  ...props
}: PageErrorFallbackProps) => {
  const t = useIntl('components.page_error_fallback')

  const RootComponent = slots?.root || Box
  const IconComponent = slots?.icon || ErrorOutlineIcon
  const RetryButtonComponent = withUseErrorBoundary ? ResetErrorBoundaryButton : RetryButton

  return (
    <RootComponent
      {...props}
      sx={mergeSx(
        {
          width: '100%',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        },
        sx,
      )}
    >
      <Grid container spacing={4} sx={{ maxWidth: 720 }}>
        <Grid size={{ xs: 12 }}>
          <Stack spacing={2} alignItems='center' textAlign='center'>
            <IconComponent
              {...slotProps?.icon}
              sx={mergeSx({ fontSize: 64, color: 'error.main' }, slotProps?.icon?.sx)}
            />
            <Typography variant='h4'>{title || t('.title')}</Typography>
            <Typography variant='body1' color='text.secondary'>
              {description || t('.description')}
            </Typography>
            {!!message && (
              <Alert severity='error' sx={{ width: '100%' }}>
                <Typography
                  variant='subtitle2'
                  sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                  {message}
                </Typography>
              </Alert>
            )}
            {children}
            <Stack direction='row' spacing={2} sx={{ pt: 1 }}>
              {!!(onRetry || withUseErrorBoundary) && <RetryButtonComponent onRetry={onRetry} />}
              {!!onGoBack && (
                <Button variant='outlined' color='inherit' onClick={onGoBack}>
                  {t('.go_back')}
                </Button>
              )}
              {!!showClearFilters && (
                <Button variant='outlined' color='inherit' onClick={handleClearFilters}>
                  {t('.clear_filters_button')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </RootComponent>
  )
}

const ResetErrorBoundaryButton = ({ onRetry }: { onRetry?: () => void }) => {
  const { resetBoundary } = useErrorBoundary()

  return (
    <RetryButton
      onRetry={() => {
        onRetry?.()
        resetBoundary()
      }}
    />
  )
}

const RetryButton = ({ onRetry }: { onRetry?: () => void }) => {
  const t = useIntl('components.page_error_fallback')

  return (
    <Button variant='contained' color='primary' onClick={onRetry}>
      {t('.refresh_button')}
    </Button>
  )
}

export default PageErrorFallback
